import { useEffect, useId, useState } from 'react';
import { useSelector } from 'react-redux';

import { defaultCountryCode } from '@components/country-code/constants';
import {
  GROUP_MEMBER_MAX_AMOUNT,
  GROUP_MEMBER_MIN_AMOUNT,
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH
} from '@constants/generic';
import { PaymentCollectionType } from '@features/group-management/constants';
import DuplicateAdmissionModal from '@features/member-management/components/DuplicateAdmissionModal';
import useNotify from '@hooks/notify';
import useActiveNotificationChannel from '@hooks/useActiveNotificationChannel';
import {
  AddMemberFormDataType,
  AddMemberModalType,
  AddMemberProps,
  MemberListType,
  SectionHeaders,
  SectionHeadersType,
  TermsList,
  ValidationError
} from '@types';

import Button from '../button/Button';
import {
  addMemberFormDataInitialState,
  getFormattedAddMemberData,
  hasChangesInMemberDetails,
  isMemberSaveDisabled
} from './helper';
import MemberAddress from './MemberAddress';
import MemberBasicInfo from './MemberBasicInfo';
import SectionHeader from './SectionHeader';

import Modal from '../../layouts/modal/Modal';

const AddMemberModal = ({
  type = AddMemberModalType.ADD,
  isOpen,
  handleCancel,
  addMember,
  paymentCollectionType,
  selectedmember,
  groupAmount,
  groupDueDate,
  groupFrequency,
  groupStartDate,
  groupEndDate,
  isFeeCategoryEnabled = false,
  feeCategoryData,
  feeCategoriesValues,
  memberList,
  isImmediatePayment = false,
  isCustomTerm = false,
  termsList = [],
  validateMember,
  isValidationLoading = false
}: AddMemberProps) => {
  const id = useId();

  const { orgConfig } = useSelector((state: any) => state.user);
  const { appConfig } = useSelector((state: any) => state.user);

  const [formData, setFormData] = useState<AddMemberFormDataType>({
    ...addMemberFormDataInitialState,
    amount: groupAmount || '',
    dueDate: {
      day: groupDueDate || '',
      paymentCollectionType,
      interval: String(groupFrequency) || '1'
    },
    startDate: groupStartDate || new Date().toString(),
    endDate: '',
    termsList,
    totalFee: selectedmember?.totalFee || ''
  });
  const [isCalendarVisible, setIsCalendarVisible] = useState<boolean>(false);
  const [memberId, setMemberId] = useState<string>('');
  const [error, setError] = useState<ValidationError>({});
  const [openSection, setOpenSection] = useState<SectionHeadersType>(
    SectionHeaders.BASICINFO
  );

  // State for v3 API validation flow
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] =
    useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<
    'save' | 'saveAndAddAnother' | null
  >(null);
  const [pendingMemberData, setPendingMemberData] =
    useState<MemberListType | null>(null);
  const [warningAdmissionNumber, setWarningAdmissionNumber] =
    useState<string>('');

  const { createErrorAlert } = useNotify();
  const { isNotificationChannelActive } = useActiveNotificationChannel();

  const groupMemberMaxAmount =
    orgConfig?.group_member_amount_max || GROUP_MEMBER_MAX_AMOUNT;
  const groupMemberMinAmount =
    orgConfig?.group_member_amount_min || GROUP_MEMBER_MIN_AMOUNT;
  const memberNameMaxLength =
    orgConfig?.member_name_length_max || NAME_MAX_LENGTH;
  const memberNameMinLength =
    orgConfig?.member_name_length_min || NAME_MIN_LENGTH;
  const isEmailEnabled = isNotificationChannelActive(
    'payment_request',
    'email'
  );

  const handleFormDataChange = (
    field: keyof typeof formData,
    value: string
  ) => {
    setFormData((prevFormData: AddMemberFormDataType) => ({
      ...prevFormData,
      [field]: value
    }));
  };

  const onLine1Change = (value: string) => {
    handleFormDataChange('addressLine1', value);
  };

  const onLine2Change = (value: string) => {
    handleFormDataChange('addressLine2', value);
  };

  const onCountryChange = (value: string) => {
    if (value === 'Others') {
      handleFormDataChange('state', '');
      handleFormDataChange('city', '');
      handleFormDataChange('pincode', '');
    }
    handleFormDataChange('country', value);
  };

  const onStateChange = (value: string) => {
    handleFormDataChange('state', value);
  };

  const onCityChange = (value: string) => {
    handleFormDataChange('city', value);
  };

  const onPincodeChange = (value: string) => {
    setError({ ...error, pincode: '' });
    handleFormDataChange('pincode', value);
  };

  const onBlurPincode = (value: string) => {
    if (value && value.length !== 6)
      setError({ ...error, pincode: 'Invalid pincode' });
  };

  const getIsDueDateInPast = (row: TermsList) => {
    const isDueDatePast =
      new Date(new Date(row.dueDate).setHours(0, 0, 0, 0)) <
      new Date(new Date().setHours(0, 0, 0, 0));
    return isDueDatePast;
  };

  const getIsTermEndDateInPast = (row: TermsList) => {
    const isTermEndDatePast =
      new Date(new Date(row.termEndDate).setHours(0, 0, 0, 0)) <
      new Date(new Date().setHours(0, 0, 0, 0));
    return isTermEndDatePast;
  };

  const validateForm = () => {
    const hasDuplicate = memberList.some(
      (member) =>
        `${member.name}_${member.primaryNumberCountryCode}${member.primaryNumber}` ===
          `${formData.name}_${formData.primaryCountryCode}${formData.primaryNumber}` &&
        member.id !== memberId
    );
    if (hasDuplicate) {
      createErrorAlert('Member already exists');
      return false;
    }
    if (isCustomTerm) {
      if (
        formData.termsList?.some(
          (term) =>
            getIsDueDateInPast(term) &&
            term.dueAmount !== '0' &&
            !getIsTermEndDateInPast(term)
        )
      ) {
        setError({ ...error, dueDate: "Due date can't be in the past" });
        return false;
      }
    }
    return true;
  };

  const getFeeConfigValue = (categoryItem: any, index: number) => {
    const category = selectedmember?.feeCategoryData?.find(
      (item) =>
        item.id === (categoryItem.branch_fee_category_id ?? categoryItem.id)
    );
    if (category?.isManuallyUpdated) return category.value;
    return (
      (feeCategoriesValues && feeCategoriesValues[index]?.value) ||
      categoryItem.amount ||
      categoryItem.value ||
      0
    );
  };

  const isFeeConfigUpdated = (categoryItem: any) => {
    const category = selectedmember?.feeCategoryData?.find(
      (item) =>
        item.id === (categoryItem.branch_fee_category_id ?? categoryItem.id)
    );
    return !!category?.isManuallyUpdated;
  };

  const resetFeeCategory = () => {
    const formattedData = feeCategoryData?.map((item: any, index: number) => ({
      id: item.branch_fee_category_id ?? item.id,
      label: item.category || item.label,
      value: getFeeConfigValue(item, index),
      isManuallyUpdated: isFeeConfigUpdated(item)
    }));

    setFormData((prev) => ({
      ...prev,
      feeCategories: formattedData || []
    }));
  };

  const getFeeConfigTotalAmount = () =>
    feeCategoryData
      ?.reduce(
        (sum, item, index) => sum + Number(getFeeConfigValue(item, index)),
        0
      )
      .toString() || '0';

  const handleReset = () => {
    setFormData({
      ...addMemberFormDataInitialState,
      amount: groupAmount || '',
      dueDate: {
        day: groupDueDate || '',
        paymentCollectionType,
        interval: String(groupFrequency) || '1'
      },
      startDate: groupStartDate || new Date().toString(),
      endDate: '',
      termsList,
      totalFee: ''
    });
    setIsCalendarVisible(false);
    setOpenSection(SectionHeaders.BASICINFO);
    resetFeeCategory();
    // Reset validation state
    setIsDuplicateModalOpen(false);
    setPendingAction(null);
    setPendingMemberData(null);
    setWarningAdmissionNumber('');
  };

  // Handle the actual save after validation
  const executeSaveAction = (
    memberData: MemberListType,
    action: 'save' | 'saveAndAddAnother'
  ) => {
    addMember(memberData);
    if (action === 'save') {
      handleCancel();
    } else {
      handleReset();
    }
  };

  // Handle save with optional v3 API validation
  const handleSaveWithValidation = async (
    action: 'save' | 'saveAndAddAnother'
  ) => {
    const isValid = validateForm();
    if (!isValid) return;

    const formattedData = getFormattedAddMemberData(
      formData,
      type,
      memberId,
      id,
      groupAmount,
      groupDueDate
    );

    // If validateMember is provided, call it for v3 API validation
    if (validateMember) {
      setIsValidating(true);
      try {
        const validationResult = await validateMember(formattedData);

        if (validationResult.hasError) {
          // Show error toast, keep modal open
          createErrorAlert(
            validationResult.errorMessage || 'Validation failed'
          );
          setIsValidating(false);
          return;
        }

        if (validationResult.hasWarning) {
          // Show duplicate modal, store pending action
          setPendingAction(action);
          setPendingMemberData(formattedData);
          setWarningAdmissionNumber(validationResult.admissionNumber || '');
          setIsDuplicateModalOpen(true);
          setIsValidating(false);
          return;
        }

        // No error, no warning - proceed with save
        setIsValidating(false);
        executeSaveAction(formattedData, action);
      } catch {
        createErrorAlert('Validation failed. Please try again.');
        setIsValidating(false);
      }
    } else {
      // No validation function provided, use current flow
      addMember(formattedData);
      if (action === 'save') {
        handleCancel();
      } else {
        handleReset();
      }
    }
  };

  const onSave = () => {
    handleSaveWithValidation('save');
  };

  const onSaveAndAddAnother = () => {
    handleSaveWithValidation('saveAndAddAnother');
  };

  // Handlers for DuplicateAdmissionModal
  const handleDuplicateModalCancel = () => {
    setIsDuplicateModalOpen(false);
    setPendingAction(null);
    setPendingMemberData(null);
    setWarningAdmissionNumber('');
  };

  const handleDuplicateModalProceed = () => {
    if (pendingMemberData && pendingAction) {
      executeSaveAction(pendingMemberData, pendingAction);
    }
    setIsDuplicateModalOpen(false);
    setPendingAction(null);
    setPendingMemberData(null);
    setWarningAdmissionNumber('');
  };

  const hasError = Object.values(error).some((value) => value !== '');

  const isSaveDisabled = isMemberSaveDisabled(
    formData,
    groupMemberMaxAmount,
    groupMemberMinAmount,
    memberNameMaxLength,
    memberNameMinLength,
    appConfig.supported_phone_number_countries,
    isFeeCategoryEnabled
  );

  const hasChanges = hasChangesInMemberDetails(formData, selectedmember);

  const onClickSectionHeader = (value: SectionHeadersType) => {
    if (openSection !== value) setOpenSection(value);
    else
      setOpenSection(
        value === SectionHeaders.BASICINFO
          ? SectionHeaders.ADDRESS
          : SectionHeaders.BASICINFO
      );
  };

  useEffect(() => {
    if (type === AddMemberModalType.EDIT) {
      setFormData((prev) => ({
        ...prev,
        name: selectedmember?.name || '',
        primaryNumber: selectedmember?.primaryNumber || '',
        primaryCountryCode:
          selectedmember?.primaryNumberCountryCode || defaultCountryCode,
        altNumber: selectedmember?.altNumber || '',
        altCountryCode:
          selectedmember?.altNumberCountryCode || defaultCountryCode,
        amount:
          isFeeCategoryEnabled && feeCategoryData && !isCustomTerm
            ? getFeeConfigTotalAmount()
            : selectedmember?.amount || groupAmount || '',
        dueDate: {
          day: selectedmember?.dueDate || groupDueDate || '',
          interval: selectedmember?.interval || String(groupFrequency) || '1',
          paymentCollectionType:
            selectedmember?.paymentCollectionType ||
            paymentCollectionType ||
            PaymentCollectionType.MONTHLY
        },
        email: selectedmember?.email || '',
        addressLine1: selectedmember?.addressLine1 || '',
        addressLine2: selectedmember?.addressLine2 || '',
        country: selectedmember?.country || '',
        state: selectedmember?.state || '',
        city: selectedmember?.city || '',
        pincode: selectedmember?.pincode || '',
        startDate: selectedmember?.startDate || '',
        endDate: selectedmember?.endDate || '',
        totalFee: selectedmember?.totalFee || ''
      }));
      setMemberId(selectedmember?.id || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    if (isCalendarVisible) {
      const element = document.getElementById('due-date-calendar');
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }
  }, [isCalendarVisible]);

  useEffect(() => {
    if (feeCategoryData) resetFeeCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(feeCategoryData)]);

  return (
    <Modal isOpen={isOpen} onCancel={handleCancel} isCloseIconRequired>
      <div
        className={`relative flex ${
          isEmailEnabled && 'h-165'
        } w-[1200px] flex-col justify-start rounded-lg bg-theme p-6`}
      >
        <div className="mb-3 text-xl font-semibold">
          {type === AddMemberModalType.EDIT ? 'Edit Member' : 'Add Member'}
        </div>
        <SectionHeader
          title="Basic Info"
          isOpen={openSection === SectionHeaders.BASICINFO}
          onClick={() => onClickSectionHeader(SectionHeaders.BASICINFO)}
        />
        {openSection === SectionHeaders.BASICINFO && (
          <MemberBasicInfo
            formData={formData}
            setError={setError}
            error={error}
            memberNameMaxLength={memberNameMaxLength}
            memberNameMinLength={memberNameMinLength}
            isFeeCategoryEnabled={isFeeCategoryEnabled}
            isImmediatePayment={isImmediatePayment}
            isCustomTerm={isCustomTerm}
            groupMemberMaxAmount={groupMemberMaxAmount}
            groupMemberMinAmount={groupMemberMinAmount}
            selectedmember={selectedmember}
            paymentCollectionType={paymentCollectionType}
            groupFrequency={groupFrequency}
            groupStartDate={groupStartDate}
            groupEndDate={groupEndDate}
            isEmailEnabled={isEmailEnabled}
            handleFormDataChange={handleFormDataChange}
            supported_phone_number_countries={
              appConfig.supported_phone_number_countries
            }
            setFormData={setFormData}
            setIsCalendarVisible={setIsCalendarVisible}
          />
        )}
        <SectionHeader
          title="Address"
          isOpen={openSection === SectionHeaders.ADDRESS}
          onClick={() => onClickSectionHeader(SectionHeaders.ADDRESS)}
          className="border-t border-t-grey30"
        />
        {openSection === SectionHeaders.ADDRESS && (
          <MemberAddress
            formData={formData}
            onLine1Change={onLine1Change}
            onLine2Change={onLine2Change}
            onCountryChange={onCountryChange}
            onStateChange={onStateChange}
            onCityChange={onCityChange}
            onPincodeChange={onPincodeChange}
            pincodeError={error.pincode}
            onBlurPincode={() => onBlurPincode(formData.pincode)}
          />
        )}
        <div className="mt-2 flex h-auto w-full gap-6">
          {type === AddMemberModalType.ADD && (
            <Button
              label="Save and add another"
              variant="secondary"
              handleClick={onSaveAndAddAnother}
              disabled={
                isSaveDisabled ||
                hasError ||
                isValidating ||
                isValidationLoading
              }
              loading={isValidating || isValidationLoading}
              className="w-full"
            />
          )}
          <Button
            label="Save"
            handleClick={onSave}
            disabled={
              isSaveDisabled ||
              !hasChanges ||
              hasError ||
              isValidating ||
              isValidationLoading
            }
            loading={isValidating || isValidationLoading}
            className={type === AddMemberModalType.EDIT ? 'w-full' : 'w-full'}
          />
        </div>
      </div>
      {/* Duplicate Admission Modal for v3 API warnings */}
      <DuplicateAdmissionModal
        isOpen={isDuplicateModalOpen}
        admissionNumber={warningAdmissionNumber}
        isAdmissionNumberError={!!warningAdmissionNumber}
        isLoading={false}
        onCancel={handleDuplicateModalCancel}
        onProceed={handleDuplicateModalProceed}
      />
    </Modal>
  );
};

export default AddMemberModal;
